import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import { getIconUrl } from '../utils/getIconUrl';
import { iconsList, RATINGS_FIXED } from './constants/constants';
import { defaultDescription, defaultTitle } from '../constants/constants';
import Header from '../common/Header.jsx';
import Footer from '../common/Footer.jsx';
import { setTabIndex } from '../../helpers/accessibility_helper';
import { useNumberKeySelectorCallback } from '../../helpers/number_key_selector_helper';
import { useKeySelect } from '../../helpers/key_select_helper';
import { DEFAULT_TRANSLATION } from '../../helpers/constants';

const useStyles = makeStyles((theme) => ({
  icon: {
    [theme.breakpoints.down('lg')]: {
      height: '3.6em',
    },
    [theme.breakpoints.up('lg')]: {
      height: '4em',
    },
  },
  iconButton: {
    paddingRight: '8px',
    cursor: 'pointer',
    outline: 'unset',
  },
  iconContainer: {
    padding: theme.spacing(1, 0),
  },
  '@keyframes blinker': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  blink: {
    animation: '$blinker 200ms linear 0s 2',
  },
}));

const IconPreview = ({
  showTextComment = false,
  questionId = -1,
  showSkipButton = false,
  handleAnswer = () => {},
  onCommentChange = () => {},
  iconType = iconsList[0].id,
  savedAnswer,
  isRequired = false,
  showDescription = false,
  title = defaultTitle,
  titleRef,
  titleAriaLabel,
  commentAriaLabel,
  description = defaultDescription,
  allowKeyboardSelection = true,
  isActive = true,
  commentsLabel,
  commentsPlaceholder,
  translation = DEFAULT_TRANSLATION,
}) => {
  const styles = useStyles();

  const MIN_OPTION = 1;
  const MAX_OPTION = 4;

  const emptyAnswer = { value: null, questionId, comment: '' };
  const [answer, setAnswer] = useState(savedAnswer || emptyAnswer);
  const [, selectAnswer] = useState(false);
  const [iconsArray, setIcons] = useState([]);

  const getIconsFromS3 = (limit, iconType) =>
    [...Array(MAX_OPTION).keys()].map((i) => {
      const iconId = i + 1;
      return getIconUrl(iconId, RATINGS_FIXED, iconType, iconId === limit);
    });

  const reColorIcons = useCallback(
    (index) => {
      const newIcons = getIconsFromS3(index, iconType);
      setIcons(newIcons);
    },
    [iconType],
  );

  useEffect(() => {
    if (savedAnswer && Object.entries(savedAnswer).length > 0) {
      reColorIcons(savedAnswer.value);
    } else {
      reColorIcons(-1);
    }
  }, [savedAnswer, reColorIcons]);

  const numberKeySelector = useNumberKeySelectorCallback(MIN_OPTION, MAX_OPTION, (key) => {
    handleAnswerSelection(key);
  });

  useKeySelect(allowKeyboardSelection, numberKeySelector);

  const handleMouseEnter = (index) => {
    reColorIcons(index);
  };

  const handleMouseLeave = (answer) => {
    reColorIcons(answer.value || -1);
  };

  const handleOptionalComment = (event) => {
    const newAnswer = { ...answer, comment: event.target.value };
    setAnswer(newAnswer);
    onCommentChange(event.target.value);
  };

  const handleAnswerSelection = (index) => {
    reColorIcons(index);
    const newAnswer = { ...answer, value: index };
    setAnswer(newAnswer);
    selectAnswer(true);
    handleAnswer(newAnswer, !showTextComment);
  };

  const onConfirm = (hasAnswer = false) => {
    if (hasAnswer) {
      handleAnswer(answer);
    } else {
      setAnswer(emptyAnswer);
      handleAnswer(emptyAnswer);
    }
  };

  return (
    <div>
      <Header
        title={title}
        titleRef={titleRef}
        titleAriaLabel={titleAriaLabel}
        questionId={questionId}
        description={description}
        showDescription={showDescription}
        isRequired={isRequired}
        isActive={isActive}
        translation={translation}
      />

      <Grid container direction="row" className={classNames(styles.iconContainer, styles.gridWidth)}>
        {iconsArray.map((iconUrl, index) => {
          const position = index + 1;

          return (
            <Button
              className={classNames(answer && position === answer.value && styles.blink, styles.iconButton)}
              key={index}
              onClick={() => handleAnswerSelection(position)}
              onMouseOver={() => handleMouseEnter(position)}
              onMouseLeave={() => handleMouseLeave(answer)}
              tabIndex={setTabIndex(isActive)}
              aria-hidden={!isActive}
              aria-label={`Click to select ${iconType}${position}`}
              aria-pressed={position === answer?.value}
            >
              <img className={styles.icon} src={iconUrl} alt={`${iconType}-${index}`} />
            </Button>
          );
        })}
      </Grid>

      <Footer
        value={answer.value}
        showSkipButton={showSkipButton && answer.value === null}
        showOkButton={showTextComment && answer.value !== null}
        onConfirm={onConfirm}
        optionalComment={answer.comment}
        onChangeComment={handleOptionalComment}
        showTextComment={showTextComment}
        commentAriaLabel={commentAriaLabel}
        isActive={isActive}
        label={commentsLabel}
        placeholder={commentsPlaceholder}
        translation={translation}
      />
    </div>
  );
};

IconPreview.propTypes = {
  questionId: PropTypes.number,
  iconType: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  handleAnswer: PropTypes.func,
  onCommentChange: PropTypes.func,
  savedAnswer: PropTypes.object,
  showTextComment: PropTypes.bool,
  showDescription: PropTypes.bool,
  showSkipButton: PropTypes.bool,
  isRequired: PropTypes.bool,
  isActive: PropTypes.bool,
  allowKeyboardSelection: PropTypes.bool,
};

export default IconPreview;
